import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Eraser, LoaderCircle, Split } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import AllocateDialog from '../components/AllocateDialog'
import ItemPicker from '../components/ItemPicker'
import MoneyLinesEditor from '../components/MoneyLinesEditor'
import { canAllocateChange, checkChangeDraftLines, toChangeLineInputs } from '../lib/changes'
import { draftFromLines, mergeAllocation, sameLines, type DraftLine } from '../lib/contracts'
import { formatMoney } from '../lib/money'
import type { CostTree } from '../lib/tree'
import type { CostChange, CostChangeKind, CostLineInput, CostLineView } from '../types'

const HINT_KEY: Record<CostChangeKind, string> = {
  BUDGET_CHANGE: 'CostControl_change_lines_hint_BUDGET_CHANGE',
  CONTRACT_CHANGE: 'CostControl_change_lines_hint_CONTRACT_CHANGE',
  TRANSFER: 'CostControl_change_lines_hint_TRANSFER',
  POTENTIAL: 'CostControl_change_lines_hint_POTENTIAL',
}

interface Props {
  open: boolean
  change: CostChange
  lines: CostLineView[]
  tree: CostTree
  currency: string
  locale: string
  onOpenChange: (open: boolean) => void
  /** Resolves true when the server accepted the lines; the dialog stays open on failure. */
  onSave: (lines: CostLineInput[]) => Promise<boolean>
}

const ChangeLinesDialog: React.FC<Props> = ({ open, change, lines, tree, currency, locale, onOpenChange, onSave }) => {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<DraftLine[]>([])
  const [allocating, setAllocating] = useState(false)
  const [saving, setSaving] = useState(false)
  const saved = lines.map((l) => ({ nodeId: l.nodeId, amount: l.amount, description: null }))

  useEffect(() => {
    if (!open) return
    setDraft(draftFromLines(saved))
    setAllocating(false)
    setSaving(false)
    // Only on opening: a background reload must not wipe the edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const check = checkChangeDraftLines(draft, change.kind)
  const unchanged = sameLines(draft, saved)
  const money = (v: string) => formatMoney(v, { locale, currency })
  const transfer = change.kind === 'TRANSFER'

  const save = async () => {
    if (check.problem || unchanged || saving) return
    setSaving(true)
    const ok = await onSave(toChangeLineInputs(draft))
    setSaving(false)
    if (ok) onOpenChange(false)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => { if (!saving) onOpenChange(next) }}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('CostControl_change_lines_dialog_title')}</DialogTitle>
            <DialogDescription>{t(HINT_KEY[change.kind])}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <ItemPicker tree={tree} excludeIds={draft.map((l) => l.nodeId)} disabled={saving} onPick={(nodeId) => setDraft((d) => [...d, { nodeId, amount: null, valid: true, description: '' }])} />
            {canAllocateChange(change.kind) && (
              <Button variant="outline" size="sm" className="h-8" onClick={() => setAllocating(true)} disabled={saving}>
                <Split className="h-4 w-4" />
                {t('CostControl_lines_allocate')}
              </Button>
            )}
            {draft.length > 0 && (
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setDraft([])} disabled={saving}>
                <Eraser className="h-4 w-4" />
                {t('CostControl_lines_clear')}
              </Button>
            )}
          </div>
          <MoneyLinesEditor lines={draft} tree={tree} disabled={saving} allowNegative showNote={false} onChange={setDraft} />
          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-sm">
            <span className={cn(transfer && !check.balanced && 'text-destructive')}>
              {t(transfer ? 'CostControl_change_balance' : 'CostControl_lines_total')}: <strong className="tabular-nums">{money(check.total)}</strong>
            </span>
          </div>
          {check.problem && draft.length > 0 && <p className="text-sm text-destructive">{t(check.problem)}</p>}
          {!check.problem && transfer && !check.balanced && <p className="text-sm text-muted-foreground">{t('CostControl_change_transfer_unbalanced_hint')}</p>}
          <div className="flex items-center justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>{t('Common_cancel')}</Button>
            <Button onClick={save} disabled={saving || !!check.problem || unchanged}>
              {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
              {t('Common_save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <AllocateDialog
        open={open && allocating}
        projectId={change.projectId}
        tree={tree}
        currency={currency}
        locale={locale}
        initialAmount={null}
        applyKey="CostControl_allocate_apply_lines"
        onOpenChange={setAllocating}
        onApply={(alloc) => setDraft((d) => mergeAllocation(d, alloc))}
      />
    </>
  )
}
export default ChangeLinesDialog
